<template>
  <div class="impact-selector" :class="{ 'impact-selector--min': min }">
    <div v-if="legend && legend !== ''" class="legend legend--bottom-margin">
      {{ legend }}
    </div>
    <div class="impact-selector__values v-centerer">
      <div
        v-for="val in availableValues"
        :key="val.val"
        class="impact-selector__values__item generic-border--clickable generic-border--blue-border"
        :class="{ 'impact-selector__values__item--selected': val.isSelected }"
        @click="valueClicked(val)"
      >
        <div>{{ min ? val.val : val.text }}</div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Component to select the value of the impact between 0 and 3.
 * It mainly consists of four buttons to select the value(s) of the impact.
 * WARNING: The impact in the DB is comprised between 0 and 3 but on the app,
 * It will be displayed between 1 and 4. This is because of major design update in december 2021
 * TODO: Delete MinMaxSelector, ImapctSlider and sliderProperties
 *
 * @vue-prop {Number|Array} value          - Impact value between 0 and 3. If multiple, array of values
 * @vue-prop {Array} hint                  - Hints to display for each value
 * @vue-prop {Boolean} [multiple=false]    - Allow selecting multiple values.
 * @vue-prop {Boolean} [min=false]    - Display the minimal version without hints.
 *
 * @vue-event {Number} input          - Emit the new value on edition
 */

export default {

  name: 'ImpactSelector',

  props: {
    hints: {
      type: Array,
      default: () => [],
    },
    value: {
      type: [Number, Array],
      required: true,
    },
    legend: {
      type: String,
      default: null,
    },
    multiple: Boolean,
    min: Boolean,
    disabled: Boolean,
  },

  computed: {
    /** Array: Array of clickable values between 1 and 4 */
    availableValues() {
      const val = this.multiple ? this.value : [this.roundedValue];
      return [0, 1, 2, 3].map((value) => {
        const hint = this.hintPerValue[`${value}`] || this.defaultHintPerValue[`${value}`];
        return {
          realValue: value,
          val: value + 1,
          text: this.$t('message.IMPACT_DISPLAY', { val: value + 1, text: hint }),
          isSelected: val.includes(value),
        };
      });
    },
    /** Number: When using single value, round it to the closest integer */
    roundedValue() {
      return this.multiple ? 0 : Math.round(this.value);
    },
    /** Object: <slider_value>: <hint_text> */
    hintPerValue() {
      return this.hints.reduce((obj, hint) => ({ ...obj, [`${hint.impact}`]: hint }), {});
    },
    /** Object: Default text displayed for each value */
    defaultHintPerValue() {
      return {
        0: this.$t('message.IMPACT_LOW'),
        1: this.$t('message.IMPACT_MODERATE'),
        2: this.$t('message.IMPACT_SIGNIFICANT'),
        3: this.$t('message.IMPACT_CRITICAL'),
      };
    },
  },

  methods: {
    /** A value was clicked in order to select or unselect it */
    valueClicked(valObject) {
      if (this.disabled) {
        return;
      }
      if (this.multiple) {
        if (this.value.includes(valObject.realValue)) {
          this.$emit('input', this.value.filter(val => val !== valObject.realValue));
        } else {
          this.$emit('input', [...this.value, valObject.realValue]);
        }
      } else {
        this.$emit('input', valObject.realValue);
      }
    },
  },

};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.impact-selector {

  & &__values {

    &__item {
      min-width: 0px;
      flex: 1;
      padding: 0 .3rem;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      border: solid 1px $color-border-grey-dark;
      height: 2.2rem;
      border-radius: $medium-border-radius;
      margin-right: 0.4em;
      font-size: 0.9rem;
      cursor: pointer;

      > div {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }

      &:last-child {
        margin-right: 0;
      }

      &--selected {
        font-weight: 600;
        border: solid 1px $color-shizen-blue-border-hover;
      }
    }
  }

  &--min {
    .impact-selector__values {

      &__item {
        flex: initial;
        width: 2.2rem;
      }
    }
  }
}
</style>
